import { useTranslation } from "react-i18next";
import { type IdAndTitle } from "~/api/types";
import { useTiers } from "~/api/swr/useTiers";
import { Link } from "react-router";
import { Button } from "~/atoms/button";
import { Label } from "~/atoms/label";

interface Props {
  tierId: number;
  parentDocuments: IdAndTitle[];
  childDocuments: IdAndTitle[];
  onLinkClick?: () => void;
}

const DocumentLinks = ({
  tierId,
  parentDocuments,
  childDocuments,
  onLinkClick,
}: Props) => {
  const { data: tiers, error } = useTiers();
  const { t } = useTranslation();

  if (error) return <div>{t(($) => $.toasts.error.generic)}</div>;
  if (tiers === undefined) return null;

  const tierOrder = tiers.findIndex((t) => t.id === tierId);
  const parentTier = tiers[tierOrder - 1];
  const childTier = tiers[tierOrder + 1];

  return (
    <div className="space-y-4 text-sm">
      {parentTier && (
        <DocumentList
          tierTitle={parentTier.title}
          documents={parentDocuments}
          noDocumentsMessage={t(($) => $.sidebar.linkedNone)}
          onLinkClick={onLinkClick}
        />
      )}
      {childTier && (
        <DocumentList
          tierTitle={childTier.title}
          documents={childDocuments}
          noDocumentsMessage={t(($) => $.sidebar.linkedNone)}
          onLinkClick={onLinkClick}
        />
      )}
    </div>
  );
};

interface DocumentListProps {
  tierTitle: string;
  documents: IdAndTitle[];
  noDocumentsMessage: string;
  onLinkClick?: () => void;
}

const DocumentList = ({
  tierTitle,
  documents,
  noDocumentsMessage,
  onLinkClick,
}: DocumentListProps) => (
  <div className="gap-2">
    <Label variant="muted">{tierTitle}</Label>
    <div className="flex min-w-0 flex-col items-start">
      {documents.map(({ id, title }) => (
        <Button variant="link" key={id} asChild onClick={onLinkClick}>
          <Link
            to={`/documents/${id}`}
            className="w-full justify-start wrap-break-word whitespace-normal!"
          >
            {title}
          </Link>
        </Button>
      ))}
      {documents.length === 0 && (
        <div className="text-muted-foreground text-xs italic">
          {noDocumentsMessage}
        </div>
      )}
    </div>
  </div>
);

export default DocumentLinks;
